import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { createStructuredSelector } from 'reselect';
import { Field, reduxForm } from 'redux-form/immutable';
import { debounce, find, get, map, isEmpty } from 'lodash';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import { Button, DialogActions, DialogContent, FormControlLabel, Radio, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { TypographyWithIcon, Dialog } from 'embeds/components';
import { DialogHeader } from 'embeds/components/DialogHeader';
import { TextFieldSearch } from 'embeds/components/TextFieldSearch';
import FormRadioGroup from 'embeds/components/ReduxForm/FormRadioGroup';
import { EmptyState } from 'embeds/components/EmptyState';

import { muiThemeLuma } from 'embeds/themes';
import { selectPermissions } from 'selectors';
import { makeSelectFormField } from 'containers/App/selectors';
import { getSquiggliesNotSupported } from 'ui-utils';

import { searchSavedTemplates } from './actions';
import { FORM_NAME } from './constants';
import { makeSelectSavedTemplates, makeSelectSearching } from './selectors';

const inlineStyles = {
  emptyState: {
    buttonLabelStyle: {
      color: muiThemeLuma.palette.common.white,
      fontSize: 12,
      fontWeight: 500,
      textTransform: 'none',
    },
    buttonStyle: {
      background: muiThemeLuma.custom.palette.colorSupportMidnight,
      width: 123,
    },
  },
};

const styles = ({
  custom: {
    palette: { colorGrayishBlue, colorSupportPowder, colorAccentLuma },
  },
  palette: {
    action: { disabled },
    common: { white },
  },
  spacing: { unit },
}) => ({
  button: {
    width: 112,
  },
  dialogContent: {
    display: 'block',
    minHeight: 245,
    overflow: 'hidden',
    padding: 0,
    paddingTop: '0px !important',
  },
  dialogFooter: {
    textAlign: 'right',
    '& button:last-child': {
      marginLeft: unit * 2,
    },
  },
  emptyMessages: {
    display: 'inline-block',
    padding: unit * 2,
    width: '100%',
  },
  field: {
    display: 'inline-block',
    overflowY: 'scroll',
    float: 'left',
    width: '100%',
  },
  searchField: {
    display: 'inline-block',
    float: 'left',
    width: '100%',
    padding: `${unit * 2}px ${unit * 3}px`,
    borderBottom: `1px solid ${colorGrayishBlue} !important`,
  },
  form: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
  },
  loading: {
    display: 'inline-block',
    padding: '10% 40% 0%',
    textAlign: 'center',
    width: '100%',
  },
  radioGroup: {
    maxHeight: 326,
    overflowY: 'auto',
    width: '100%',
    '& > div': {
      flexDirection: 'row',
    },
  },
  radioItem: {
    backgroundColor: white,
    borderBottom: `1px solid ${disabled}`,
    display: 'inline-block',
    margin: '0px !important',
    padding: `${unit}px ${unit * 3}px ${unit}px ${unit}px`,
    width: '100%',
    '&$selected': {
      backgroundColor: `${colorSupportPowder} !important`,
    },
    '&$disabled': {
      opacity: 0.3,
    },
  },
  savedTemplateRadio: {
    display: 'inline-block',
    float: 'left',
  },
  savedTemplateInfo: {
    display: 'inline-block',
    float: 'left',
    width: 'calc(100% - 48px)',
  },
  savedTemplateMessage: {
    backgroundColor: white,
    border: '1px solid #B8C7CC',
    fontSize: 14,
    fontWeight: 500,
    height: 74,
    marginTop: unit,
    overflow: 'auto',
    padding: unit,
    userSelect: 'none',
  },
  savedTemplateName: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '48px',
  },
  warningIcon: {
    width: 30,
    marginTop: -1 * unit * 2,
  },
  selected: {},
  disabled: {},
});

const messages = defineMessages({
  warning: {
    id: 'containers.DialogSavedTemplatesSelector.warning',
    defaultMessage:
      'To use a grayed-out message, please update your file and mapping to ensure it includes the information for all the squigglies in the message.',
  },
});

class DialogSavedTemplatesSelector extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
    };

    this.renderSavedTemplateOption = this.renderSavedTemplateOption.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { dispatchSearchSavedTemplates, initialize, open, savedTemplates, type } = this.props;

    if (!open && nextProps.open) dispatchSearchSavedTemplates('', type);

    if (savedTemplates !== nextProps.savedTemplates) {
      if (nextProps.savedTemplates) {
        const savedTemplate = find(nextProps.savedTemplates, (template) =>
          isEmpty(getSquiggliesNotSupported(template.message, nextProps.squiggliesSupported, true)),
        );
        initialize({ savedTemplate: get(savedTemplate, '_id') });
      } else {
        initialize({ savedTemplate: false });
      }
    }
  }

  handleInputSearchChange = (value) => {
    const { dispatchSearchSavedTemplates, type } = this.props;

    this.setState({ searchText: value });

    dispatchSearchSavedTemplates(value, type);
  };

  handleCreateMessageClick = () => {
    const { onClose, type } = this.props;

    onClose();
    browserHistory.push(`/embed/settings/utilities/message-library/${type}`);
  };

  handleSubmit = (data) => {
    const { onClose, savedTemplates } = this.props;
    const body = data.toJS();

    if (body.savedTemplate) {
      onClose(find(savedTemplates, { _id: body.savedTemplate }));
    }
  };

  renderDialogFooter() {
    const { classes, onClose, handleSubmit, savedTemplates, valid } = this.props;
    const disabled = !savedTemplates || savedTemplates.length === 0 || !valid;

    return (
      <DialogActions className={classes.dialogFooter} disableActionSpacing>
        {!disabled ? (
          <React.Fragment>
            <Button className={classes.button} color="primary" form={FORM_NAME} onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              className={classes.button}
              color="primary"
              disabled={disabled}
              form={FORM_NAME}
              onClick={handleSubmit(this.handleSubmit)}
              type="submit"
              variant="contained"
            >
              Select
            </Button>
          </React.Fragment>
        ) : (
          <Button
            className={classes.button}
            color="primary"
            form={FORM_NAME}
            onClick={() => onClose()}
            variant="contained"
          >
            Close
          </Button>
        )}
      </DialogActions>
    );
  }

  renderFieldSearch() {
    const { classes, savedTemplates, searching, squiggliesSupported, includeEmptySquiggliesWarning } = this.props;
    const { searchText } = this.state;

    if ((!savedTemplates || savedTemplates.length === 0) && searchText === '' && !searching) return;

    const slowSearch = debounce(this.handleInputSearchChange, 300);

    return (
      <div className={classes.searchField}>
        {!isEmpty(squiggliesSupported) && includeEmptySquiggliesWarning && (
          <TypographyWithIcon
            paragraph
            icon={
              <span className={classes.warningIcon} role="img" aria-label="warning">
                ⚠️
              </span>
            }
          >
            <FormattedMessage {...messages.warning} />
          </TypographyWithIcon>
        )}
        <TextFieldSearch placeholder="Search" size="small" onChange={slowSearch} />
      </div>
    );
  }

  renderSavedTemplateInfo(item) {
    const { classes } = this.props;
    const messageHtml = item.message
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br />')
      .replace(/$/g, '<br />')
      .replace(/&lt;br&gt;/g, '<br />')
      .replace(/&lt;br \/&gt;/g, '<br />')
      .replace(
        /({{[^}]+}})/gm,
        `<span style="background-color: #f1f1f1; border-radius: 4px; color: ${muiThemeLuma.custom.palette.colorDarkCyan}; font-weight: 400;">$1</span>`,
      );

    return (
      <div className={classes.savedTemplateInfo}>
        <Typography className={classes.savedTemplateName} color="primary" component="span">
          {item.name}
        </Typography>
        <Typography
          className={classes.savedTemplateMessage}
          color="primary"
          component="span"
          dangerouslySetInnerHTML={{ __html: messageHtml }}
          paragraph
        />
        {item.autoCloseChat && (
          <TypographyWithIcon icon={<InfoIcon fontSize="small" />} gutterBottom>
            This message is set to automatically close patient chat
          </TypographyWithIcon>
        )}
      </div>
    );
  }

  renderSavedTemplateOption(item) {
    const { classes, formValues, squiggliesSupported } = this.props;
    const body = formValues && formValues.toJS();

    // Disable item in case it contains squigglies that are not supported
    const disabled = !isEmpty(getSquiggliesNotSupported(item.message, squiggliesSupported, true));

    return (
      <FormControlLabel
        className={classnames(classes.radioItem, {
          [classes.selected]: body && body.savedTemplate === item._id,
          [classes.disabled]: disabled,
        })}
        control={<Radio className={classes.savedTemplateRadio} color="primary" />}
        key={item._id}
        label={this.renderSavedTemplateInfo(item)}
        value={item._id}
        disabled={disabled}
      />
    );
  }

  renderFieldTemplate() {
    const { classes, savedTemplates, permissions, searching } = this.props;
    const { searchText } = this.state;
    const hasPermission = get(permissions, 'isAdmin') || get(permissions, 'isMaster') || get(permissions, 'isManager');

    if (!searching) {
      if (!savedTemplates) return <div />;

      if (savedTemplates.length === 0) {
        if (searchText === '') {
          return hasPermission ? (
            <EmptyState
              buttonLabelStyle={inlineStyles.emptyState.buttonLabelStyle}
              buttonStyle={inlineStyles.emptyState.buttonStyle}
              buttonText="Create message"
              icon=":floppy_disk:"
              onClickButton={this.handleCreateMessageClick}
              subtitle="So you only have to fuss over your messaging once."
              title="Create your first message"
            />
          ) : (
            <EmptyState
              icon=":floppy_disk:"
              subtitle="Please contact your admin to set up your Message Library"
              title="There are currently no messages in your library"
            />
          );
        }

        return (
          <Typography className={classes.emptyMessages} color="primary" component="span" variant="subtitle1">
            No message templates were found. Check your spelling or try a different search.
          </Typography>
        );
      }
    }

    return (
      <div className={classes.field}>
        <Field
          className={classes.radioGroup}
          component={FormRadioGroup}
          name="savedTemplate"
          onChangeCustom={this.handleSavedTemplateChange}
        >
          {map(savedTemplates, this.renderSavedTemplateOption)}
        </Field>
      </div>
    );
  }

  renderDialogContent() {
    const { classes, handleSubmit } = this.props;

    return (
      <DialogContent className={classes.dialogContent}>
        <form autoComplete="off" className={classes.form} onSubmit={handleSubmit(this.handleSubmit)}>
          {this.renderFieldSearch()}
          {this.renderFieldTemplate()}
        </form>
      </DialogContent>
    );
  }

  render() {
    const { onClose, open } = this.props;

    return (
      <Dialog open={open} onClose={() => onClose()} preventSubmit>
        <DialogHeader onRequestClose={() => onClose()} title="Select message from library" />
        {this.renderDialogContent()}
        {this.renderDialogFooter()}
      </Dialog>
    );
  }
}

DialogSavedTemplatesSelector.propTypes = {
  classes: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  dispatchSearchSavedTemplates: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  open: PropTypes.bool,
  squiggliesSupported: PropTypes.arrayOf(PropTypes.string),
  savedTemplates: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  type: PropTypes.string.isRequired,
  searching: PropTypes.bool,
  valid: PropTypes.bool.isRequired,
  includeEmptySquiggliesWarning: PropTypes.bool,
};

DialogSavedTemplatesSelector.defaultProps = {
  squiggliesSupported: [],
  savedTemplates: false,
  searching: false,
  includeEmptySquiggliesWarning: false,
};

const mapStateToProps = createStructuredSelector({
  permissions: selectPermissions,
  formValues: makeSelectFormField(FORM_NAME),
  savedTemplates: makeSelectSavedTemplates(),
  searching: makeSelectSearching(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchSearchSavedTemplates: (text, type) => {
    dispatch(searchSavedTemplates(text, type));
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withForm = reduxForm({ form: FORM_NAME });

export default compose(withConnect, withForm, withStyles(styles))(DialogSavedTemplatesSelector);
